import React, { useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import {
  GET_INVENTORY_INGREDIENTS,
  GET_BRANCHES,
  REPORT_WASTE,
} from "./queries";
import {
  InventoryIngredient,
  LOCATION_OPTIONS,
  WASTE_REASON_LABELS,
} from "./models";

const ReportWaste: React.FC = () => {
  const history = useHistory();

  const [ingredientId, setIngredientId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [location, setLocation] = useState("kitchen");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [search, setSearch] = useState("");

  const [fetchIngredients, { data: ingredientData, loading: loadingIngredients }] =
    useLazyQuery(GET_INVENTORY_INGREDIENTS, { fetchPolicy: "no-cache" });
  const [fetchBranches, { data: branchData }] = useLazyQuery(GET_BRANCHES, {
    fetchPolicy: "no-cache",
  });
  const [reportWaste, { loading: submitting }] = useMutation(REPORT_WASTE);

  useEffect(() => {
    fetchBranches();
    fetchIngredients({ variables: { take: 200 } });
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchIngredients({ variables: { search: search || undefined, take: 50 } });
    //eslint-disable-next-line
  }, [search]);

  const selectedIngredient: InventoryIngredient | null =
    ingredientData?.getInventoryIngredients?.find(
      (i: InventoryIngredient) => i.id === ingredientId
    ) || null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!ingredientId || !branchId || !quantity || !reason) {
      toast.error(
        lang(
          "Completa todos los campos requeridos",
          "Fill in all required fields"
        )
      );
      return;
    }

    try {
      await reportWaste({
        variables: {
          ingredient_id: ingredientId,
          branch_id: parseInt(branchId),
          quantity: parseFloat(quantity),
          location,
          reason,
          notes: notes || undefined,
        },
      });
      toast.success(
        lang("Merma registrada correctamente", "Waste reported successfully")
      );
      history.push("/inventory/waste");
    } catch (err: any) {
      toast.error(err.message || lang("Error al registrar", "Error reporting"));
    }
  };

  return (
    <>
      <div className="d-flex align-items-center mb-4">
        <button
          className="btn btn-light mr-3"
          onClick={() => history.push("/inventory/waste")}
        >
          <i className="fa fa-arrow-left" />
        </button>
        <h4 className="mb-0 font-weight-bold">
          {lang("Registrar Merma", "Report Waste")}
        </h4>
      </div>

      <div className="row">
        <div className="col-lg-8">
          <div className="material_card p-4">
            <form onSubmit={handleSubmit}>
              {/* Ingredient Search */}
              <div className="form-group">
                <label className="font-weight-bold">
                  {lang("Ingrediente", "Ingredient")} *
                </label>
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder={lang(
                    "Buscar ingrediente...",
                    "Search ingredient..."
                  )}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {loadingIngredients && <LoaderDots />}
                <select
                  className="custom-select"
                  value={ingredientId}
                  onChange={(e) => setIngredientId(e.target.value)}
                  size={5}
                >
                  {ingredientData?.getInventoryIngredients?.map(
                    (i: InventoryIngredient) => (
                      <option key={i.id} value={i.id}>
                        {i.name} ({i.unit})
                        {i.total_stock != null
                          ? ` — Stock: ${Number(i.total_stock).toFixed(2)}`
                          : ""}
                      </option>
                    )
                  )}
                </select>
                {selectedIngredient && (
                  <div className="mt-2 small text-muted">
                    {lang("Seleccionado", "Selected")}:{" "}
                    <strong>{selectedIngredient.name}</strong> (
                    {selectedIngredient.unit})
                    {selectedIngredient.cost != null && (
                      <> — {lang("Costo", "Cost")}: ${Number(selectedIngredient.cost).toFixed(2)}/{selectedIngredient.unit}</>
                    )}
                  </div>
                )}
              </div>

              {/* Branch */}
              <div className="form-group">
                <label className="font-weight-bold">
                  {lang("Sucursal", "Branch")} *
                </label>
                <select
                  className="custom-select"
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                >
                  <option value="">
                    {lang("Seleccionar sucursal", "Select branch")}
                  </option>
                  {branchData?.getBranches?.map((b: any) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Quantity */}
              <div className="form-group">
                <label className="font-weight-bold">
                  {lang("Cantidad", "Quantity")} *
                  {selectedIngredient && (
                    <span className="font-weight-normal text-muted ml-1">
                      ({selectedIngredient.unit})
                    </span>
                  )}
                </label>
                <input
                  type="number"
                  className="form-control"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>

              {/* Location */}
              <div className="form-group">
                <label className="font-weight-bold">
                  {lang("Ubicación", "Location")}
                </label>
                <select
                  className="custom-select"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                >
                  {LOCATION_OPTIONS.map((loc) => (
                    <option key={loc.value} value={loc.value}>
                      {loc.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Reason */}
              <div className="form-group">
                <label className="font-weight-bold">
                  {lang("Razón", "Reason")} *
                </label>
                <select
                  className="custom-select"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                >
                  <option value="">
                    {lang("Seleccionar razón", "Select reason")}
                  </option>
                  {Object.entries(WASTE_REASON_LABELS).map(([key, val]) => (
                    <option key={key} value={key}>
                      {lang(val.label, val.labelEn)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Notes */}
              <div className="form-group">
                <label className="font-weight-bold">
                  {lang("Notas", "Notes")}
                </label>
                <textarea
                  className="form-control"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder={lang(
                    "Notas adicionales (opcional)",
                    "Additional notes (optional)"
                  )}
                />
              </div>

              {/* Cost Preview */}
              {selectedIngredient?.cost != null && quantity && (
                <div className="alert alert-warning mb-3">
                  <strong>{lang("Costo estimado", "Estimated cost")}:</strong>{" "}
                  $
                  {(
                    parseFloat(quantity) * Number(selectedIngredient.cost)
                  ).toFixed(2)}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                className="btn btn-danger btn-block"
                disabled={submitting}
              >
                {submitting ? (
                  <LoaderDots />
                ) : (
                  <>
                    <i className="fa fa-exclamation-triangle mr-2" />
                    {lang("Registrar Merma", "Report Waste")}
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReportWaste;
